(() => {
  if (customElements.get('ni-recipe-shop')) return;
  class RecipeShop extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      this.rows = [...this.querySelectorAll('[data-material]')];
      this.status = this.querySelector('[data-status]');
      this.form = this.querySelector('form');
      const number = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
      this.money = { format: value => number.format(value) + ' kr.' };
      this.rows.forEach(row => this.setup(row));
      this.form.addEventListener('submit', event => { event.preventDefault(); this.add(this.rows.filter(row => row.querySelector('[data-include]').checked)); });
      this.updateTotal();
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.track('view'); observer.disconnect(); }
      }, { threshold: .15 });
      observer.observe(this);
    }
    track(action, extra = {}) {
      if (this.dataset.preview === 'true') return;
      const data = { recipe: this.dataset.recipe, ...extra };
      try { window.Shopify?.analytics?.publish('ni:recipe_' + action, data); } catch (_) {}
    }
    setup(row) {
      row.variants = JSON.parse(row.querySelector('[data-variants]').textContent);
      row.selects = [...row.querySelectorAll('[data-option]')];
      const suggested = row.variants.find(v => String(v.id) === row.dataset.default);
      row.selects.forEach((select, index) => {
        const isSize = /stærð|size/i.test(select.dataset.optionName);
        if (row.dataset.pickSize === 'true' && isSize) select.value = '';
        else select.value = suggested?.options[index] || '';
        if (row.dataset.colorLock && /litur|color|colour/i.test(select.dataset.optionName)) {
          select.value = row.dataset.colorLock;
          [...select.options].forEach(option => { if (option.value && option.value !== row.dataset.colorLock) option.hidden = true; });
        }
        select.addEventListener('change', () => { select.removeAttribute('aria-invalid'); this.update(row); this.track('option_selected', { material: row.querySelector('h3').textContent.trim() }); });
      });
      row.querySelector('[data-include]').addEventListener('change', () => this.update(row));
      row.querySelector('[data-quantity]').addEventListener('input', () => this.updateTotal());
      row.querySelector('[data-add-one]').addEventListener('click', () => this.add([row]));
      if (suggested && !suggested.available && row.dataset.pickSize !== 'true') row.querySelector('[data-include]').checked = false;
      this.update(row);
    }
    variant(row) {
      if (!row.selects || !row.variants) return null;
      if (!row.selects.length) return row.variants[0];
      const options = row.selects.map(select => select.value);
      if (options.some(value => !value)) return null;
      return row.variants.find(variant => variant.options.every((value, i) => value === options[i]));
    }
    update(row) {
      row.selects.forEach((select, index) => {
        const previous = row.selects.slice(0, index).map(other => other.value);
        [...select.options].forEach(option => {
          if (!option.value) return;
          const possible = row.variants.some(v => v.available && v.options[index] === option.value && previous.every((value, i) => !value || v.options[i] === value) && (!row.dataset.colorLock || v.options.some(value => value === row.dataset.colorLock)));
          option.disabled = !possible;
        });
      });
      const variant = this.variant(row);
      row.querySelector('[data-availability]').textContent = variant ? (variant.available ? this.strings.available : this.strings.soldOut) : this.strings.choose;
      row.querySelector('[data-price]').textContent = variant ? this.money.format(variant.price / 100) : '—';
      row.querySelector('[data-add-one]').disabled = !variant?.available || this.busy;
      row.dataset.excluded = String(!row.querySelector('[data-include]').checked);
      this.updateTotal();
    }
    updateTotal() {
      let total = 0;
      this.rows.forEach(row => {
        const variant = this.variant(row), qty = Number(row.querySelector('[data-quantity]').value);
        if (row.querySelector('[data-include]').checked && variant?.available && Number.isInteger(qty) && qty > 0) total += variant.price * qty;
      });
      this.querySelector('[data-total]').textContent = this.money.format(total / 100);
    }
    async add(rows) {
      if (this.busy) return;
      if (!rows.length) { this.status.textContent = this.strings.empty; this.status.focus(); return; }
      let invalid = null, error = '';
      const items = [];
      for (const row of rows) {
        const variant = this.variant(row), input = row.querySelector('[data-quantity]'), quantity = Number(input.value);
        if (!variant?.available) { invalid = row.selects.find(select => !select.value) || row.selects[0]; error = variant ? this.strings.soldOut : this.strings.choose; break; }
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) { invalid = input; error = this.strings.quantity; break; }
        const properties = { _ni_recipe: this.dataset.recipe };
        if (this.dataset.preview === 'true') properties._ni_test = 'recipe_preview';
        items.push({ id: variant.id, quantity, properties });
      }
      if (error) { this.status.textContent = error; if (invalid) { invalid.setAttribute('aria-invalid', 'true'); invalid.focus(); } return; }
      this.busy = true;
      this.dataset.busy = 'true';
      this.status.textContent = this.strings.adding;
      this.querySelectorAll('button').forEach(button => { button.disabled = true; });
      const root = window.Shopify?.routes?.root || '/';
      const drawer = document.querySelector('cart-drawer');
      const sectionIds = drawer ? drawer.getSectionsToRender().map(section => section.id) : ['cart-icon-bubble'];
      let posted = false;
      try {
        const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items, sections: sectionIds, sections_url: location.pathname }) });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error('cart_rejected');
        posted = true;
        this.status.textContent = this.strings.added;
        this.track('added', { item_count: items.length });
        const bubble = data.sections?.['cart-icon-bubble'];
        if (bubble) {
          const html = new DOMParser().parseFromString(bubble, 'text/html');
          const current = document.getElementById('cart-icon-bubble'), next = html.querySelector('#cart-icon-bubble');
          if (current && next) current.innerHTML = next.innerHTML;
        }
        if (drawer && data.sections?.['cart-drawer']) {
          drawer.setActiveElement(document.activeElement);
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ni-recipe-shop', cartData: data });
      } catch (_) {
        this.status.textContent = posted ? this.strings.added : this.strings.error + ' ' + this.strings.review;
        if (!posted) this.track('add_failed');
      } finally {
        this.busy = false;
        this.dataset.busy = 'false';
        this.querySelector('[type=submit]').disabled = false;
        this.rows.forEach(row => this.update(row));
      }
    }
  }
  customElements.define('ni-recipe-shop', RecipeShop);
})();
